/**
 * Fleet hardware inventory - the opsanio-owned NetBox devices, both the ones
 * assigned to an appliance and the ones still in the spare pool.
 *
 * "In use" is derived from the `opsanioinuse` tag, the same marker the
 * questionnaire sync sets/clears (see NetBoxProcessDeviceSync /
 * NetBoxReleaseDevices), so free hardware here means exactly what the device
 * pickers in the wizard offer.
 */

export type OpsanioHardwareKind = "server" | "switch" | "dac";

/** Slim projection of a NetBox device for inventory views. */
export interface OpsanioHardwareItem {
  id: number;
  kind: OpsanioHardwareKind;
  name: string;
  model: string;
  manufacturer: string;
  role: string;
  serial: string;
  /** NetBox status value (e.g. "active", "inventory", "offline"). */
  status: string;
  statusLabel: string;
  site: string | null;
  siteId: number | null;
  tenant: string | null;
  tags: Array<{ name: string; slug: string }>;
  /** Carries the `opsanioinuse` tag, i.e. assigned to an appliance. */
  inUse: boolean;
  /** Deep link into NetBox. */
  url: string;
  /** Specs from NetBox custom fields; null when the field is unset. */
  cpuCores: number | null;
  memoryGb: number | null;
  storageGb: number | null;
}

export interface OpsanioHardwareCounts {
  total: number;
  inUse: number;
  free: number;
  /** Defective: NetBox status `failed` (e.g. after a hardware case). Not free,
   *  not counted as assigned. total = inUse + free + failed. */
  failed: number;
}

export interface OpsanioHardwareSummary {
  server: OpsanioHardwareCounts;
  switch: OpsanioHardwareCounts;
  dac: OpsanioHardwareCounts;
  total: OpsanioHardwareCounts;
}

export interface OpsanioHardwareInventory {
  items: OpsanioHardwareItem[];
  summary: OpsanioHardwareSummary;
  /** Kinds whose NetBox lookup failed - their counts are missing, not zero. */
  failed: OpsanioHardwareKind[];
}

/**
 * Every opsanio-owned device carries both tags. Shared rather than server-only
 * because the device-list links are built from the same filter the inventory
 * lookup uses - two copies would drift, and a link that filters differently
 * from the count it sits next to is worse than no link.
 */
export const OPSANIO_HARDWARE_TAGS = ["autonubil", "opsaniohardware"];

export const OPSANIO_HARDWARE_IN_USE_TAG = "opsanioinuse";

/** Marks a device as standing at the customer's premise. */
export const OPSANIO_HARDWARE_CUSTOMER_LOCATION_TAG = "opsaniolocationcustomer";

/**
 * The device tags every appliance-assigned device must carry. `opsanioinuse`
 * takes it out of the spare pool; `opsaniolocationcustomer` records that it
 * physically stands at the customer.
 *
 * These two - and only these two - are controlled by the sync. Any other tag a
 * device carries (`autonubil`, `opsaniohardware`, anything set by hand) is
 * preserved: the sync adds, it never removes.
 */
/**
 * Marks a single COMPONENT - an inventory item, not a device - as failed
 * hardware. A drive carrying this is skipped when the disk layout is derived,
 * so a machine that has lost a disk installs on the disks it actually has
 * instead of failing on a device that is not there.
 *
 * Tag the inventory item, not the device: the device is still in service.
 */
export const OPSANIO_HARDWARE_DEFECT_TAG = "defect";

export const OPSANIO_ASSIGNED_DEVICE_TAGS = [
  OPSANIO_HARDWARE_IN_USE_TAG,
  OPSANIO_HARDWARE_CUSTOMER_LOCATION_TAG,
];

// ---------------------------------------------------------------------------
// Appliance kit + reorder
// ---------------------------------------------------------------------------

/**
 * One line of the bill of materials for a single appliance.
 *
 * `kind` links the line to the NetBox inventory so the free pool can be counted
 * against it. It is `null` for items opsanio orders but does **not** track as
 * NetBox devices - power cables have no device type, so their stock is unknown
 * rather than zero and must never be rendered as "0 free".
 */
export interface OpsanioKitLine {
  key: string;
  kind: OpsanioHardwareKind | null;
  label: string;
  /** How many of this item one complete appliance needs. */
  perAppliance: number;
  /**
   * What to order when NetBox cannot say - either because the item is no device
   * type at all (power cables) or because the lookup failed. A label alone
   * ("DAC cables") is not orderable; a supplier needs a part number, a model
   * name or at minimum the one property that must be right.
   */
  spec?: OpsanioHardwareSpec;
}

/**
 * The orderable identity of one kit line: what a supplier is quoted on.
 *
 * Mirrors the NetBox device-type fields (`part_number`, `model`,
 * `description`), because that is where these are maintained - ordering off a
 * label typed into anketa is how the wrong transceiver arrives.
 *
 * Every field is a plain string and may be empty: a device type without a part
 * number states nothing rather than inventing one.
 */
export interface OpsanioHardwareSpec {
  /** NetBox `part_number` - the manufacturer's ordering number. */
  partNumber: string;
  /** NetBox `model` - the model name. */
  model: string;
  /** NetBox `description`. */
  description: string;
  /** NetBox manufacturer name; empty when unknown. */
  manufacturer?: string;
  /** Deep link to the NetBox device type this was read from. */
  url?: string;
  /** NetBox device-type id, when the spec came from NetBox. */
  deviceTypeId?: number;
  /**
   * The device type's `purchase_price` custom field, **including sales tax** -
   * that is how the figures are maintained in NetBox. `null` when the type
   * carries no price, which is a different statement from "free" and must never
   * be rendered as 0.
   */
  purchasePrice?: number | null;
  /**
   * Why this spec is not the live NetBox one - "not a NetBox device type", "the
   * lookup failed". Set only on a fallback, so the mail can say so instead of
   * presenting stale text as current.
   */
  fallbackReason?: string;
}

/** What must be ordered to cover one appliance. */
export const OPSANIO_APPLIANCE_KIT: OpsanioKitLine[] = [
  { key: "server", kind: "server", label: "Servers", perAppliance: 3 },
  { key: "switch", kind: "switch", label: "Switch", perAppliance: 1 },
  { key: "dac", kind: "dac", label: "DAC cables", perAppliance: 6 },
  {
    key: "power-c13-c14",
    kind: null,
    label: "C13 ⇔ C14 power cables",
    perAppliance: 6,
    // No NetBox device type exists for these, and only one property actually
    // has to be right when they are ordered: the length.
    spec: {
      partNumber: "",
      model: "C13/C14",
      description: "Kaltgerätekabel C13 auf C14, Länge 2 m",
      fallbackReason: "kein NetBox-Gerätetyp - maßgeblich ist die Länge 2 m",
    },
  },
];

/**
 * NetBox device-type id behind a configured device-type URL.
 *
 * Admins paste the page they are looking at
 * (`https://netbox.medocino.net/dcim/device-types/157/`); the API needs the id
 * out of it. A bare id is accepted too, because that is the other thing people
 * type into a field called "device type".
 *
 * Returns `null` for anything that is not a device-type reference rather than
 * guessing - a wrong id silently orders the wrong hardware. Where a kind has
 * several configured types, this is the first - the one that gets ordered; see
 * {@link deviceTypeIdsFromUrl}.
 */
export const deviceTypeIdFromUrl = (value: unknown): number | null =>
  deviceTypeIdsFromUrl(value)[0] ?? null;

/**
 * Every device-type id in a setting, in the order they were configured.
 *
 * A hardware kind is not one device type for long: a second DAC (Aruba J9281D
 * beside the FS SFP-10G-PC-02) or a new server generation lands in the fleet
 * while the old one is still deployed, and a filter pinned to a single id makes
 * the new hardware unselectable in the questionnaire rather than merely
 * unusual. So the settings hold a **list**, separated by commas, semicolons or
 * newlines.
 *
 * **The first entry is the one ordered** - it is what the hardware request mail
 * quotes a part number for - while all of them count as that kind in the
 * inventory and in the device pickers.
 *
 * Entries that are not device-type references are skipped rather than guessed
 * at, and duplicates collapse, so a stray comma cannot silently widen a filter.
 */
export const deviceTypeIdsFromUrl = (value: unknown): number[] => {
  if (typeof value !== "string") return [];
  const ids: number[] = [];
  for (const entry of value.split(/[\s,;]+/)) {
    const raw = entry.trim();
    if (raw === "") continue;

    let id: number | null = null;
    if (/^\d+$/.test(raw)) {
      id = parseInt(raw, 10);
    } else {
      // Both the UI page (/dcim/device-types/157/) and the API path
      // (/api/dcim/device-types/157/) end in the id.
      const match = raw.match(/\/dcim\/device-types\/(\d+)/);
      if (match) id = parseInt(match[1], 10);
    }
    if (id !== null && id > 0 && !ids.includes(id)) ids.push(id);
  }
  return ids;
};

/**
 * Link to the NetBox device list showing exactly the devices a stock figure was
 * counted from.
 *
 * "3 of 8 free" is a number someone has to be able to check, and the only place
 * that can settle it is the filtered table itself - retyping the tag and role
 * filters by hand is how a reader ends up looking at a different set than the
 * one the figure came from. The filters mirror `hardwareKindFilters()` /
 * `OPSANIO_HARDWARE_TAGS` one for one.
 *
 * `baseUrl` may be any URL on the instance - the configured device-type page is
 * the usual one - because only its origin is used. An unparseable base returns
 * `""`, and the caller then renders the figure without a link rather than a
 * broken one.
 */
export const netboxDeviceListUrl = ({
  baseUrl,
  roleSlugs = [],
  deviceTypeIds = [],
  freeOnly = false,
}: {
  baseUrl: string;
  roleSlugs?: string[];
  deviceTypeIds?: number[];
  /** Restrict to the spare pool, i.e. exclude `opsanioinuse`. */
  freeOnly?: boolean;
}): string => {
  let origin = "";
  try {
    origin = new URL(baseUrl).origin;
  } catch {
    return "";
  }
  if (origin === "" || origin === "null") return "";

  const params = new URLSearchParams();
  // Both tags, like the inventory lookup: NetBox ANDs repeated distinct keys
  // and ORs repeated values of the same key, which is what `listDevicesByTag`
  // relies on too.
  for (const tag of OPSANIO_HARDWARE_TAGS) params.append("tag", tag);
  for (const role of roleSlugs) params.append("role", role);
  for (const id of deviceTypeIds) params.append("device_type_id", String(id));
  // `tag__n` is NetBox's negated tag filter - the list equivalent of the
  // `inUse` split the summary counts with.
  if (freeOnly) params.append("tag__n", OPSANIO_HARDWARE_IN_USE_TAG);

  return `${origin}/dcim/devices/?${params.toString()}`;
};

/** Per-kind view of how far the free pool stretches. */
export interface OpsanioBuildableLine {
  key: string;
  label: string;
  perAppliance: number;
  /** Free devices of this kind, or `null` when the kind is not tracked. */
  free: number | null;
  /** How many appliances this line alone could cover, `null` when untracked. */
  covers: number | null;
}

export interface OpsanioBuildable {
  /** Complete appliances that can be assembled from free hardware right now. */
  appliances: number;
  lines: OpsanioBuildableLine[];
  /** Keys of the lines that cap `appliances` - what to order first. */
  limitedBy: string[];
}

/**
 * How many complete appliances the free pool still covers.
 *
 * The scarcest tracked line wins: 9 free servers, 1 free switch and 30 free DACs
 * build **one** appliance, not three. Untracked lines (power cables) cannot cap
 * the figure - counting an unknown stock as zero would permanently report
 * "0 buildable" and make the number useless.
 */
export const buildableAppliances = (
  summary: OpsanioHardwareSummary,
  /** Kinds whose NetBox lookup failed; they cannot be counted as zero either. */
  failed: OpsanioHardwareKind[] = [],
): OpsanioBuildable => {
  const lines: OpsanioBuildableLine[] = OPSANIO_APPLIANCE_KIT.map((line) => {
    const tracked = line.kind !== null && !failed.includes(line.kind);
    const free = tracked ? (summary[line.kind]?.free ?? 0) : null;
    return {
      key: line.key,
      label: line.label,
      perAppliance: line.perAppliance,
      free,
      covers:
        free === null || line.perAppliance <= 0
          ? null
          : Math.floor(free / line.perAppliance),
    };
  });

  const counted = lines.filter(
    (line): line is OpsanioBuildableLine & { covers: number } =>
      line.covers !== null,
  );
  if (counted.length === 0) {
    return { appliances: 0, lines, limitedBy: [] };
  }

  const appliances = Math.min(...counted.map((line) => line.covers));
  return {
    appliances,
    lines,
    limitedBy: counted
      .filter((line) => line.covers === appliances)
      .map((line) => line.key),
  };
};

/** What one kit line still needs, after the free pool is taken into account. */
export interface OpsanioKitShortfallLine {
  key: string;
  label: string;
  perAppliance: number;
  /** Gross requirement: `appliances × perAppliance`. */
  needed: number;
  /** Free devices that can cover it, `null` when stock is unknown. */
  free: number | null;
  /** What actually has to be ordered. */
  order: number;
}

/**
 * What must be ordered to reach `appliances` complete appliances, **net of the
 * free pool**.
 *
 * Ordering gross would buy hardware that is already on the shelf: two appliances
 * need six servers, but with seven free servers the answer is zero servers and
 * only the switches and DACs are missing.
 *
 * Lines whose stock is unknown - power cables (not NetBox devices) and kinds
 * whose lookup failed - are ordered **gross**. Netting against an assumed stock
 * would silently under-order; a duplicate cable is cheaper than a blocked build.
 */
export const kitShortfall = (
  appliances: number,
  summary: OpsanioHardwareSummary,
  failed: OpsanioHardwareKind[] = [],
): OpsanioKitShortfallLine[] => {
  const count = Number.isFinite(appliances) && appliances > 0 ? appliances : 0;
  return OPSANIO_APPLIANCE_KIT.map((line) => {
    const tracked = line.kind !== null && !failed.includes(line.kind);
    const free = tracked ? (summary[line.kind]?.free ?? 0) : null;
    const needed = count * line.perAppliance;
    return {
      key: line.key,
      label: line.label,
      perAppliance: line.perAppliance,
      needed,
      free,
      order: free === null ? needed : Math.max(0, needed - free),
    };
  });
};

/** One line of a hardware order as the request mail renders it. */
export interface OpsanioHardwareRequestLine {
  key: string;
  label: string;
  quantity: number;
  /**
   * Part number, model name and description of what is being ordered - the
   * live NetBox device type where there is one, the kit's static spec
   * otherwise. Absent only until {@link withHardwareSpecs} has run.
   */
  spec?: OpsanioHardwareSpec;
}

/**
 * Attach the orderable identity to each line: the resolved NetBox device type
 * where the lookup produced one, the kit's own spec where it did not.
 *
 * Never invents: a line NetBox could not answer for and that carries no static
 * spec comes back without one, and the mail then shows the label alone rather
 * than a part number nobody can be held to.
 *
 * Specs are keyed by kit key and looked up server-side - like the labels, they
 * must not come from the request, because they are rendered into a mail that
 * goes out under the company's name.
 */
export const withHardwareSpecs = (
  lines: OpsanioHardwareRequestLine[],
  specs: Record<string, OpsanioHardwareSpec | null | undefined>,
): OpsanioHardwareRequestLine[] =>
  lines.map((line) => {
    const resolved = specs[line.key];
    if (resolved) return { ...line, spec: resolved };
    const fallback = OPSANIO_APPLIANCE_KIT.find(
      (kit) => kit.key === line.key,
    )?.spec;
    return fallback ? { ...line, spec: fallback } : { ...line };
  });

/** One-line rendering of a spec for plain-text mail and audit lines. */
export const hardwareSpecSummary = (
  spec: OpsanioHardwareSpec | undefined,
): string =>
  spec
    ? [
        spec.partNumber ? `P/N ${spec.partNumber}` : "",
        spec.model,
        spec.description,
      ]
        .filter((part) => (part ?? "").trim() !== "")
        .join(" · ")
    : "";

/** Body of `POST /netbox/hardware/request`. */
export interface OpsanioHardwareRequest {
  lines: OpsanioHardwareRequestLine[];
  /** Free-text context for inside sales (deadline, customer, ticket). */
  note?: string;
}

/** Largest quantity one request may order per line. */
export const HARDWARE_REQUEST_MAX_QUANTITY = 999;
/** Longest free-text note accepted on a request. */
export const HARDWARE_REQUEST_MAX_NOTE = 2000;

export interface OpsanioHardwareRequestValidation {
  lines: OpsanioHardwareRequestLine[];
  note: string;
  errors: string[];
}

/**
 * Validate and normalize a submitted hardware order.
 *
 * Labels are taken from {@link OPSANIO_APPLIANCE_KIT} by key and never from the
 * request: the result is rendered into a mail, so letting the caller supply the
 * text would let them write arbitrary content into a message that goes out under
 * the company's name.
 *
 * Rejects rather than silently repairs - a clamped quantity would send inside
 * sales an order nobody placed. Lines with quantity 0 are simply not ordered and
 * are dropped without an error; an order with no line at all is an error.
 */
export const validateHardwareRequest = (
  input: unknown,
): OpsanioHardwareRequestValidation => {
  const errors: string[] = [];
  const lines: OpsanioHardwareRequestLine[] = [];
  const body = (input ?? {}) as Record<string, unknown>;

  const rawLines = Array.isArray(body.lines) ? body.lines : null;
  if (!rawLines) {
    return { lines: [], note: "", errors: ["lines must be an array"] };
  }

  const seen = new Set<string>();
  for (const raw of rawLines) {
    const entry = (raw ?? {}) as Record<string, unknown>;
    const key = typeof entry.key === "string" ? entry.key : "";
    const kitLine = OPSANIO_APPLIANCE_KIT.find((line) => line.key === key);
    if (!kitLine) {
      errors.push(`unknown item "${key}"`);
      continue;
    }
    if (seen.has(key)) {
      errors.push(`duplicate item "${key}"`);
      continue;
    }
    seen.add(key);

    const quantity = Number(entry.quantity);
    if (!Number.isInteger(quantity) || quantity < 0) {
      errors.push(`quantity for "${key}" must be a non-negative integer`);
      continue;
    }
    if (quantity > HARDWARE_REQUEST_MAX_QUANTITY) {
      errors.push(
        `quantity for "${key}" exceeds ${HARDWARE_REQUEST_MAX_QUANTITY}`,
      );
      continue;
    }
    if (quantity === 0) continue;
    lines.push({ key, label: kitLine.label, quantity });
  }

  const rawNote = typeof body.note === "string" ? body.note.trim() : "";
  if (rawNote.length > HARDWARE_REQUEST_MAX_NOTE) {
    errors.push(`note exceeds ${HARDWARE_REQUEST_MAX_NOTE} characters`);
  }

  if (errors.length === 0 && lines.length === 0) {
    errors.push("nothing to order");
  }

  return { lines, note: rawNote, errors };
};

// ---------------------------------------------------------------------------
// Appliance hardware sync (manual re-run of the questionnaire-save workflow)
// ---------------------------------------------------------------------------

/**
 * One field the sync would change (or did change) on one device.
 *
 * `tags` is **additive only**: `to` lists the slugs that will be *added*, never
 * a replacement set. The sync owns exactly `opsanioinuse` and
 * `opsaniolocationcustomer`; every other tag on the device is left untouched.
 */
export interface OpsanioSyncFieldChange {
  field: "name" | "tenant" | "site" | "tags";
  /** Current NetBox value, `null` when unset. For `tags`: the tags already set. */
  from: string | null;
  /** Value the convention requires. For `tags`: the slugs added on top. */
  to: string;
}

/** The planned (or applied) work for a single assigned device. */
export interface OpsanioSyncDevicePlan {
  deviceId: number;
  kind: OpsanioHardwareKind;
  /** Current NetBox name, so the UI can show what is being renamed. */
  currentName: string;
  serial: string;
  /** Empty when the device already matches every convention. */
  changes: OpsanioSyncFieldChange[];
  /**
   * Why no name is planned even though one is expected - e.g. a server whose
   * serial matches no agent in the questionnaire. Surfaced instead of silently
   * leaving the device unnamed.
   */
  warning?: string;
  /** Set on an applied run: the sync failed for this device. */
  error?: string;
}

/**
 * Result of `POST /netbox/appliance/:name/sync`. `dryRun` distinguishes the
 * preview from the run that actually wrote to NetBox.
 */
/** One addressing or cabling step of the sync. */
export interface OpsanioSyncNetworkAction {
  action: string;
  /** NetBox already held it - the run changed nothing. */
  existed: boolean;
  ok: boolean;
  error?: string;
}

/**
 * The addressing and cabling half of the sync: the node bonds, the IP
 * addresses on their interfaces, and the cables to the switch.
 *
 * Separate from `devices`, which only covers the conventions on the device
 * record itself (name, tenant, site, tags). An appliance can conform perfectly
 * there and still have no addresses and no cabling at all.
 */
export interface OpsanioSyncNetwork {
  lags: OpsanioSyncNetworkAction[];
  addresses: OpsanioSyncNetworkAction[];
  cables: OpsanioSyncNetworkAction[];
  warnings: string[];
}

export interface OpsanioSyncPlan {
  appliance: string;
  dryRun: boolean;
  devices: OpsanioSyncDevicePlan[];
  /** Absent on plans built before addressing and cabling were synced. */
  network?: OpsanioSyncNetwork;
  /** Devices selected on the questionnaire that NetBox does not know. */
  missing: number[];
  /** Total field changes across all devices; 0 = everything already conforms. */
  changeCount: number;
}
